/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Cyberpunk NOC Console Layout for Admin Backoffice (Task 6.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useEffect } from 'react';
import {
  Activity,
  Server,
  Database,
  Radio,
} from 'lucide-react';
import { AdminHeader } from '../../components/layout/AdminHeader';
import { AdminSidebar, type AdminViewId } from '../../components/layout/AdminSidebar';
import { useI18n } from '../../i18n';

export interface CyberpunkLayoutProps {
  activeView: AdminViewId;
  onViewChange: (view: AdminViewId) => void;
  children: React.ReactNode;
}

export const CyberpunkLayout: React.FC<CyberpunkLayoutProps> = ({
  activeView,
  onViewChange,
  children,
}) => {
  const { t } = useI18n();

  // Real-time UTC Clock
  const [utcTime, setUtcTime] = useState<string>('');

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setUtcTime(now.toISOString().substring(11, 19) + ' UTC');
    };
    updateTime();
    const timer = setInterval(updateTime, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="theme-cyberpunk-noc flex h-screen w-screen overflow-hidden bg-background-base text-foreground font-mono selection:bg-primary selection:text-white">
      {/* 1. Cyberpunk NOC Left Navigation Sidebar */}
      <div className="relative flex flex-col h-full border-r border-primary/30 shadow-[0_0_25px_rgba(255,0,128,0.12)] z-10">
        {/* Glow accent indicator line */}
        <div className="absolute top-0 right-0 bottom-0 w-[1px] bg-gradient-to-b from-primary via-accent to-primary opacity-60" />
        
        {/* Top NOC Badge */}
        <div className="h-6 bg-surface-elevated/80 border-b border-surface-border px-4 flex items-center justify-between text-[9px] uppercase tracking-wider text-muted-fg font-mono">
          <span className="flex items-center gap-1.5 text-accent">
            <Radio className="w-2.5 h-2.5 animate-pulse" />
            NOC MONITORED
          </span>
          <span className="text-primary font-bold">CF EDGE</span>
        </div>

        <AdminSidebar
          activeView={activeView}
          onViewChange={onViewChange}
          className="border-none flex-1"
        />
      </div>

      {/* 2. Main Executive & Observability Viewport */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative">
        {/* Custom Cyberpunk NOC Top Header Bar */}
        <header className="h-16 border-b border-primary/25 bg-surface/90 backdrop-blur flex items-center justify-between px-6 select-none z-10 shadow-[0_4px_20px_rgba(0,0,0,0.3)]">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-accent animate-ping opacity-75" />
              <span className="text-xs font-mono font-bold tracking-widest text-accent uppercase">
                // NOC CORE //
              </span>
            </div>
            <div className="h-4 w-px bg-surface-border" />
            <h1 className="font-mono font-bold text-sm tracking-tight text-foreground uppercase">
              {t(`admin.nav.${activeView}` as any, undefined, activeView)}
            </h1>
          </div>

          <div className="flex items-center gap-4">
            {/* Edge PoP Colocation pill */}
            <div className="hidden md:flex items-center gap-2 px-2.5 py-1 rounded-lg bg-surface-elevated border border-surface-border text-[11px] font-mono text-muted-fg">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              <span>EDGE COLO:</span>
              <span className="font-bold text-foreground">SGN (PoP 330+)</span>
              <span className="text-accent font-bold">~9ms</span>
            </div>

            <AdminHeader activeView={activeView} className="border-none bg-transparent p-0 h-auto" />
          </div>
        </header>

        {/* Scrollable Work Area: font-sans for rich data readability & chart harmony */}
        <main className="flex-1 overflow-y-auto p-6 font-sans">
          <div className="max-w-7xl mx-auto space-y-6">
            {children}
          </div>
        </main>

        {/* 3. Global Cloudflare Edge Node Status Footer Bar */}
        <footer className="h-8 border-t border-primary/25 bg-surface/95 backdrop-blur px-6 flex items-center justify-between text-[11px] font-mono text-muted-fg z-20 select-none shadow-[0_-2px_15px_rgba(0,0,0,0.2)]">
          <div className="flex items-center gap-4 overflow-hidden">
            {/* Edge Node Status */}
            <span className="flex items-center gap-1.5 text-emerald-400 font-semibold">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span>EDGE NODE: OK (330+ PoPs)</span>
            </span>

            <span className="hidden sm:inline-block text-surface-border">•</span>

            {/* D1 Status */}
            <span className="hidden sm:flex items-center gap-1.5 text-foreground/80">
              <Database className="w-3 h-3 text-accent" />
              <span>D1: CONNECTED (0.8ms)</span>
            </span>

            <span className="hidden md:inline-block text-surface-border">•</span>

            {/* KV Cache Status */}
            <span className="hidden md:flex items-center gap-1.5 text-foreground/80">
              <Server className="w-3 h-3 text-primary" />
              <span>KV: SYNCHRONIZED</span>
            </span>

            <span className="hidden lg:inline-block text-surface-border">•</span>

            {/* AI Gateway Status */}
            <span className="hidden lg:flex items-center gap-1.5 text-foreground/80">
              <Activity className="w-3 h-3 text-accent" />
              <span>AI GATEWAY: READY</span>
            </span>
          </div>

          <div className="flex items-center gap-3 text-[10px]">
            {utcTime && (
              <span className="hidden sm:inline font-bold text-accent">
                {utcTime}
              </span>
            )}
            <span className="hidden sm:inline text-surface-border">|</span>
            <span className="font-semibold text-foreground/90 uppercase tracking-wider">
              SOPHIA PLATFORM v2.0 • NOC EDITION
            </span>
          </div>
        </footer>
      </div>
    </div>
  );
};
